// Multi-Connections Manager - Gestion de toutes les intégrations
// Permet d'ajouter/configurer plusieurs connexions du même type
// Chaque connexion peut être liée à une activité ou asset spécifique

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Plus,
  Settings,
  Trash2,
  Check,
  X,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Shield,
  ExternalLink,
  Star,
  ChevronDown,
  ChevronRight,
  FileText,
  Globe,
  Phone,
  MessageSquare,
  Mail,
  Cloud,
  BarChart3,
  Linkedin,
  Facebook,
  Instagram,
  Play,
  CheckCircle,
  XCircle,
  LayoutGrid,
  List,
  CreditCard,
  Bot,
  Send,
  Calendar
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import IntegrationRotation from "../../components/integrations/IntegrationRotation";
import { smtpModeForPort, smtpModeLabel, smtpFlagsForPort, smtpSenderDisplayName } from "../../utils/smtpMode";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// --- SMTP / Mandrill : garde-fou expéditeur ---------------------------------
// Domaines connus NON vérifiés DKIM sur le compte Mandrill partagé → un envoi
// "From" sur ces domaines est rejeté par Mandrill ("unsigned"). On signale/empêche
// uniquement quand le host EST Mandrill (un SMTP tenant propre n'est pas concerné).
const MANDRILL_UNSIGNED_DOMAINS = ["omvfrance.fr"];
const MANDRILL_SIGNED_HINT = "contact@omnihub-ia.fr";
const isMandrillHost = (host) => (host || "smtp.mandrillapp.com").toLowerCase().includes("mandrill");
const fromEmailMandrillIssue = (host, fromEmail) => {
  if (!isMandrillHost(host)) return null;
  const dom = ((fromEmail || "").split("@")[1] || "").toLowerCase();
  if (dom && MANDRILL_UNSIGNED_DOMAINS.includes(dom)) {
    return `Le domaine « ${dom} » n'est pas signé DKIM sur Mandrill → l'email serait rejeté ("unsigned"). `
      + `Mettez un expéditeur sur un domaine signé (ex : ${MANDRILL_SIGNED_HINT}) et placez ${fromEmail} en Reply-To.`;
  }
  return null;
};

// mail_senders (boîte sortante) → objet "connexion" pour l'affichage unifié de
// la carte SMTP. L'écran écrit dans mail_senders (POST /api/mail/senders) ; il
// doit donc LIRE mail_senders ici (et non integration_connections).
const senderToConn = (s) => ({
  id: s.id,
  integration_type: "smtp",
  // Nom affiché = "SMTP (Opérateur)" dérivé du host (IONOS/Mandrill/…) — le `name`
  // stocké du sender n'est pas écrasé (reste éditable, identifiant = id).
  name: smtpSenderDisplayName(s.smtp?.host),
  is_default: !!s.is_default,
  status: s.enabled === false ? "inactive" : "active",
  scope_display: { activity: s.tenant_id ? `Tenant : ${s.tenant_id}` : "Global" },
  _source: "mail_senders",
  _sender: s,
});

// Mapping des icônes
const ICON_MAP = {
  "file-text": FileText,
  "globe": Globe,
  "bar-chart": BarChart3,
  "phone": Phone,
  "message-square": MessageSquare,
  "mail": Mail,
  "cloud": Cloud,
  "linkedin": Linkedin,
  "facebook": Facebook,
  "instagram": Instagram,
  "credit-card": CreditCard,
  "bot": Bot,
  "send": Send,
  "calendar": Calendar,
};

// Mapping des couleurs
const COLOR_MAP = {
  green: "text-green-400 bg-green-500/10",
  emerald: "text-emerald-400 bg-emerald-500/10",
  blue: "text-blue-400 bg-blue-500/10",
  purple: "text-purple-400 bg-purple-500/10",
  red: "text-red-400 bg-red-500/10",
  orange: "text-orange-400 bg-orange-500/10",
  pink: "text-pink-400 bg-pink-500/10",
  amber: "text-amber-400 bg-amber-500/10",
  gray: "text-zinc-400 bg-zinc-500/10",
  violet: "text-violet-400 bg-violet-500/10",
  rose: "text-rose-400 bg-rose-500/10",
};

// Composant pour rafraîchir le token Zoho
function ZohoTokenRefreshSection({ onSuccess }) {
  const [authCode, setAuthCode] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  
  const handleRefresh = async () => {
    if (!authCode.trim()) {
      setResult({ success: false, message: "Entrez le code d'autorisation" });
      return;
    }
    
    setRefreshing(true);
    setResult(null);
    
    try {
      const res = await axios.post(`${API}/api/integrations/zoho/refresh-token`, {
        authorization_code: authCode.trim()
      });
      
      if (res.data.success) {
        setResult({ success: true, message: res.data.message });
        setAuthCode("");
        if (onSuccess) onSuccess();
      } else {
        setResult({ success: false, message: res.data.error });
      }
    } catch (err) {
      setResult({ 
        success: false, 
        message: err.response?.data?.error || err.response?.data?.detail || err.message 
      });
    } finally {
      setRefreshing(false);
    }
  };
  
  return (
    <div className="mt-4 pt-4 border-t border-amber-500/20">
      <div 
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-amber-400 hover:text-amber-300 transition-colors w-full text-left cursor-pointer"
      >
        <RefreshCw className="w-4 h-4" />
        <span className="font-medium">Rafraîchir le token Zoho</span>
        <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </div>
      
      {expanded && (
        <div className="mt-3 p-3 bg-amber-500/5 rounded-lg border border-amber-500/20 space-y-3">
          <p className="text-xs text-zinc-400">
            Si le token est expiré, générez un nouveau code d'autorisation depuis{" "}
            <a 
              href="https://api-console.zoho.com/" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-blue-400 hover:underline inline-flex items-center gap-1"
            >
              Zoho API Console <ExternalLink className="w-3 h-3" />
            </a>
          </p>
          
          <div className="flex gap-2">
            <Input
              value={authCode}
              onChange={(e) => setAuthCode(e.target.value)}
              placeholder="1000.xxxxxxx.xxxxxxx"
              className="bg-zinc-900 border-white/10 text-white text-sm flex-1"
            />
            <Button 
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing || !authCode.trim()}
              className="bg-amber-600 hover:bg-amber-700 whitespace-nowrap"
            >
              {refreshing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Rafraîchir
                </>
              )}
            </Button>
          </div>
          
          {result && (
            <div className={`p-2 rounded text-xs ${
              result.success 
                ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400' 
                : 'bg-red-500/10 border border-red-500/20 text-red-400'
            }`}>
              {result.message}
            </div>
          )}
          
          <p className="text-[10px] text-zinc-500">
            ⚠️ Le code expire en 3 minutes. Collez-le rapidement après génération.
          </p>
        </div>
      )}
    </div>
  );
}

// D20 — Section « Autorité / Secrets » : source unique = collection integrations
// (GET /api/integrations/authority). Rotation des secrets globaux SANS bascule de
// tenant, ciblage explicite par _id. Ne lit JAMAIS integration_connections (copies).
// Première brique de D13 exigence 5 (source unique de vérité).
function AuthoritySecretsSection() {
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const AUTH_LABEL = { zoho_workdrive: "Zoho WorkDrive", pennylane: "Pennylane", google_search_console: "Google Search Console", google_calendar: "Google Calendar", google_analytics: "Google Analytics", twilio_sms: "Twilio SMS", twilio_voice: "Twilio Voice", docusign: "DocuSign" };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await axios.get(`${API}/api/integrations/authority`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true,
      });
      setFiches(res.data?.integrations || []);
    } catch (e) {
      setFiches([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !fiches.length) return null;

  const renderFingerprint = (f) => {
    if (f.fingerprint_state === "ok") {
      return <span style={{ fontSize: 12, fontFamily: "monospace", color: "var(--oh-text-secondary)" }}>empreinte {f.fingerprint}</span>;
    }
    if (f.fingerprint_state === "undecryptable") {
      return <span style={{ fontSize: 12, color: "var(--oh-stat-amber-text)" }}>empreinte indisponible — secret non déchiffrable</span>;
    }
    if (f.fingerprint_state === "empty") {
      return <span style={{ fontSize: 12, color: "var(--oh-text-muted)" }}>aucun secret configuré</span>;
    }
    return null;
  };

  return (
    <div data-testid="authority-secrets" style={{ padding: 16, borderRadius: 12, background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, color: "var(--oh-text)", fontWeight: 500 }}>
        <Shield className="w-4 h-4" />
        <span>Autorité / Secrets</span>
      </div>
      <p style={{ fontSize: 13, color: "var(--oh-text-secondary)", marginBottom: 12, lineHeight: 1.6 }}>
        Secrets administrés depuis l'autorité (collection integrations). Seule la rotation Zoho est disponible ici ; les autres intégrations convergeront dans le chantier D13. La rotation n'est proposée que si elle agit sur la source réellement utilisée par le runtime.
      </p>
      <div style={{ display: "grid", gap: 12 }}>
        {fiches.map((f) => (
          <div key={f.id || f.integration_type} data-testid={`auth-row-${f.integration_type}`} style={{ padding: 12, borderRadius: 8, border: "0.5px solid var(--oh-border)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ color: "var(--oh-text)", fontWeight: 500 }}>{AUTH_LABEL[f.integration_type] || f.integration_type}</span>
              {f.configured && (
                <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 6, background: f.is_global ? "var(--oh-stat-blue-bg)" : "var(--oh-stat-amber-bg)", color: f.is_global ? "var(--oh-stat-blue-text)" : "var(--oh-stat-amber-text)" }}>
                  {f.is_global ? "Global" : `tenant : ${f.tenant_id}`}
                </span>
              )}
              {renderFingerprint(f)}
            </div>
            {f.eligible ? (
              <IntegrationRotation integrationType={f.integration_type} ficheId={f.id} onSuccess={load} />
            ) : (
              <p data-testid={`auth-unavailable-${f.integration_type}`} style={{ marginTop: 8, fontSize: 13, color: "var(--oh-text-secondary)" }}>
                {f.unavailable_message}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function MultiConnectionsManager() {
  const [types, setTypes] = useState([]);
  // ⚠️ #926 — les catégories viennent du REGISTRE, ⛔ plus d'une liste locale.
  const [categories, setCategories] = useState([]);
  const [connections, setConnections] = useState([]);
  const [activities, setActivities] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  
  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [editSenderOpen, setEditSenderOpen] = useState(false);
  const [selectedSender, setSelectedSender] = useState(null);
  
  // Expanded sections (for list view)
  const [expandedTypes, setExpandedTypes] = useState({});

  const loadData = useCallback(async () => {
    try {
      const [typesRes, categoriesRes, connectionsRes, activitiesRes, assetsRes, tenantsRes, sendersRes] = await Promise.all([
        axios.get(`${API}/api/connections/types`),
        axios.get(`${API}/api/connections/categories`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/connections`),
        axios.get(`${API}/api/marketing/config/activities`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/marketing/config/assets`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/platform/tenants`).catch(() => ({ data: [] })),
        axios.get(`${API}/api/mail/senders`).catch(() => ({ data: { senders: [] } })),
      ]);

      setTypes(typesRes.data);
      // ⚠️ #926 — un tableau vide déclenche le repli « tout sous une rubrique »,
      // ⛔ jamais un écran vide.
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      setActivities(activitiesRes.data);
      setAssets(assetsRes.data);
      setTenants(Array.isArray(tenantsRes.data) ? tenantsRes.data : (tenantsRes.data?.tenants || []));

      // SMTP : source = mail_senders (ce que LIT l'envoi). On exclut les éventuelles
      // entrées integration_connections type=smtp (coquilles) pour ne pas doublonner,
      // et on affiche les boîtes sortantes mappées en "connexions".
      const senderList = sendersRes.data?.senders || [];
      const nonSmtpConns = (connectionsRes.data || []).filter(c => c.integration_type !== "smtp");
      setConnections([...nonSmtpConns, ...senderList.map(senderToConn)]);
      
      // Expand all types that have connections
      const expanded = {};
      typesRes.data.forEach(t => {
        const hasConnections = connectionsRes.data.some(c => c.integration_type === t.type);
        if (hasConnections) expanded[t.type] = true;
      });
      setExpandedTypes(expanded);
      
    } catch (err) {
      console.error("Erreur chargement:", err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const toggleExpand = (type) => {
    setExpandedTypes(prev => ({ ...prev, [type]: !prev[type] }));
  };

  const getConnectionsByType = (type) => {
    return connections.filter(c => c.integration_type === type);
  };

  const openAddDialog = (type) => {
    setSelectedType(type);
    setAddDialogOpen(true);
  };

  const openEditDialog = (connection) => {
    // SMTP (mail_senders) → éditeur de boîte sortante (PUT/DELETE /api/mail/senders).
    if (connection._source === "mail_senders") {
      setSelectedSender(connection._sender);
      setEditSenderOpen(true);
      return;
    }
    setSelectedConnection(connection);
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  // 🔴 #926 — LA LISTE EN DUR A ÉTÉ SUPPRIMÉE.
  // Elle vivait ici, en parallèle du registre backend : DEUX SOURCES qui
  // divergeaient EN SILENCE, et rien ne le signalait. Mesuré : 20 types au
  // registre, 20 listés — ⛔ pas les mêmes. `anah_amo` INVISIBLE à l'écran,
  // `pennylane` FANTÔME (listé, absent du registre).
  //
  // ⛔ Ajouter une 11ᵉ ligne à une liste que personne ne synchronise n'aurait
  // rien réparé : c'est la SECONDE SOURCE qui est le défaut.
  //
  // ⇒ Les catégories, leur libellé ET leur ordre viennent désormais de
  // `GET /api/connections/categories`. Un type nouveau au registre apparaît
  // NATURELLEMENT ; un type retiré DISPARAÎT — ⛔ aucune exception à écrire.
  const categoriesAffichees = categories.length
    ? categories
    // ⚠️ Repli : si la route ne répond pas, on regroupe TOUT sous une seule
    // rubrique plutôt que de n'afficher RIEN. ⛔ Un connecteur qu'on ne sait pas
    // classer ne doit jamais disparaître — c'est ce qui est arrivé à `anah_amo`.
    : [{ cle: "autres", libelle: "Intégrations" }];

  return (
    <div className="space-y-6" data-testid="multi-connections-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Intégrations</h1>
          <p className="text-zinc-400 mt-1">
            Gérez vos connexions par activité et asset
          </p>
        </div>
        <div className="flex gap-2">
          {/* Toggle Vue */}
          <div className="flex bg-zinc-800/50 rounded-lg p-1">
            <Button
              variant={viewMode === "grid" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="h-8"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="h-8"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="border-zinc-700"
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-2xl font-bold text-white">{connections.length}</p>
          <p className="text-sm text-zinc-400">Connexions actives</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-2xl font-bold text-white">
            {new Set(connections.map(c => c.integration_type)).size}
          </p>
          <p className="text-sm text-zinc-400">Types utilisés</p>
        </div>
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4">
          <p className="text-2xl font-bold text-white">{types.length}</p>
          <p className="text-sm text-zinc-400">Types disponibles</p>
        </div>
      </div>

      {/* D20 — Section Autorité / Secrets (source unique = integrations, sans tenant) */}
      <AuthoritySecretsSection />

      {/* VUE GRILLE */}
      {viewMode === "grid" && (
        <div className="space-y-5">
          {/* Intégrations actives */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-emerald-400"></div>
              <h3 className="text-sm font-medium text-zinc-300">Intégrations configurées</h3>
              <span className="text-xs text-zinc-500">({connections.length})</span>
            </div>
            
            {connections.length === 0 ? (
              <p className="text-sm text-zinc-500 italic">Aucune connexion configurée</p>
            ) : (
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                {connections.map(conn => {
                  const typeInfo = types.find(t => t.type === conn.integration_type) || {};
                  const Icon = ICON_MAP[typeInfo.icon] || Globe;
                  const colorClass = COLOR_MAP[typeInfo.color] || COLOR_MAP.gray;
                  
                  return (
                    <div
                      key={conn.id}
                      className="relative p-3 bg-zinc-900/50 rounded-lg border border-emerald-500/30 hover:border-emerald-500/50 transition-all duration-200 cursor-pointer group"
                      onClick={() => openEditDialog(conn)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${colorClass.split(" ")[1]}`}>
                          <Icon className={`w-5 h-5 ${colorClass.split(" ")[0]}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium text-sm truncate">{conn.name}</h3>
                          <p className="text-xs text-zinc-500 line-clamp-1">{conn._source === "mail_senders" ? conn._sender?.email : (conn.scope_display?.activity || conn.scope_display?.global || "Global")}</p>
                        </div>
                        <div className="flex-shrink-0">
                          {conn.is_default ? (
                            <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                          ) : (
                            <div className="w-2 h-2 rounded-full bg-emerald-400" title="Connecté" />
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Séparateur */}
          <div className="border-t border-zinc-800"></div>
          
          {/* Types disponibles (non configurés) */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-2 h-2 rounded-full bg-purple-400"></div>
              <h3 className="text-sm font-medium text-zinc-300">Types disponibles</h3>
              <span className="text-xs text-zinc-500">({types.length})</span>
            </div>
            
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
              {types.map(type => {
                const Icon = ICON_MAP[type.icon] || Globe;
                const colorClass = COLOR_MAP[type.color] || COLOR_MAP.gray;
                const hasConnection = connections.some(c => c.integration_type === type.type);
                // SMTP : toujours ajoutable (un sender par tenant) — pas de grisage.
                const canAdd = type.type === "smtp" || !hasConnection;

                return (
                  <div
                    key={type.type}
                    className={`relative p-3 bg-zinc-900/50 rounded-lg border transition-all duration-200 cursor-pointer group ${
                      !canAdd
                        ? "border-zinc-700/50 opacity-60"
                        : "border-zinc-700/50 hover:border-purple-500/30"
                    }`}
                    onClick={() => canAdd && openAddDialog(type)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${colorClass.split(" ")[1]}`}>
                        <Icon className={`w-5 h-5 ${colorClass.split(" ")[0]}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-zinc-300 font-medium text-sm truncate">{type.name}</h3>
                        <p className="text-xs text-zinc-500 line-clamp-1">{type.description?.slice(0, 30)}...</p>
                      </div>
                      {hasConnection && !canAdd ? (
                        <Badge className="bg-emerald-500/20 text-emerald-400 text-[10px]">Actif</Badge>
                      ) : (
                        <Plus className="w-4 h-4 text-zinc-500 group-hover:text-purple-400" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* VUE LISTE (par catégorie) */}
      {viewMode === "list" && (
        <div className="space-y-6">
        {categoriesAffichees.map((category) => {
          // 🔑 LE REGROUPEMENT VIENT DU TYPE LUI-MÊME. ⛔ Plus aucune liste
          // d'appartenance côté écran : c'est le registre qui classe.
          // ⚠️ `categoriesAffichees.length === 1` = le repli : on montre TOUT.
          const categoryTypes = categoriesAffichees.length === 1
            ? types
            : types.filter(t => (t.category || "autres") === category.cle);
          if (categoryTypes.length === 0) return null;

          return (
            <div key={category.cle}>
              <h2 className="text-sm font-medium text-zinc-400 uppercase tracking-wide mb-3">
                {category.libelle}
              </h2>
              
              <div className="space-y-2">
                {categoryTypes.map(type => {
                  const typeConnections = getConnectionsByType(type.type);
                  const isExpanded = expandedTypes[type.type];
                  const Icon = ICON_MAP[type.icon] || Globe;
                  const colorClass = COLOR_MAP[type.color] || COLOR_MAP.gray;
                  
                  return (
                    <div
                      key={type.type}
                      className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden"
                    >
                      {/* Header du type */}
                      <div
                        className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-800/30 transition-colors"
                        onClick={() => toggleExpand(type.type)}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass.split(" ")[1]}`}>
                            <Icon className={`w-5 h-5 ${colorClass.split(" ")[0]}`} />
                          </div>
                          <div>
                            <h3 className="font-medium text-white">{type.name}</h3>
                            <p className="text-xs text-zinc-500">{type.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {typeConnections.length > 0 && (
                            <Badge className="bg-emerald-500/20 text-emerald-400">
                              {typeConnections.length} connexion{typeConnections.length > 1 ? "s" : ""}
                            </Badge>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-zinc-700 h-8"
                            onClick={(e) => { e.stopPropagation(); openAddDialog(type); }}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Ajouter
                          </Button>
                          {isExpanded ? (
                            <ChevronDown className="w-5 h-5 text-zinc-500" />
                          ) : (
                            <ChevronRight className="w-5 h-5 text-zinc-500" />
                          )}
                        </div>
                      </div>
                      
                      {/* Liste des connexions */}
                      {isExpanded && typeConnections.length > 0 && (
                        <div className="border-t border-zinc-800 bg-zinc-800/20">
                          {typeConnections.map(conn => (
                            <ConnectionRow
                              key={conn.id}
                              connection={conn}
                              onEdit={() => openEditDialog(conn)}
                              onRefresh={loadData}
                            />
                          ))}
                        </div>
                      )}
                      
                      {/* Message si aucune connexion */}
                      {isExpanded && typeConnections.length === 0 && (
                        <div className="border-t border-zinc-800 p-4 text-center">
                          <p className="text-sm text-zinc-500">
                            Aucune connexion configurée
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* Dialog Ajout */}
      {selectedType && (
        <AddConnectionDialog
          open={addDialogOpen}
          onClose={() => { setAddDialogOpen(false); setSelectedType(null); }}
          type={selectedType}
          activities={activities}
          assets={assets}
          tenants={tenants}
          onSuccess={loadData}
        />
      )}

      {/* Dialog Edit */}
      {selectedConnection && (
        <EditConnectionDialog
          open={editDialogOpen}
          onClose={() => { setEditDialogOpen(false); setSelectedConnection(null); }}
          connection={selectedConnection}
          activities={activities}
          assets={assets}
          onSuccess={loadData}
        />
      )}

      {/* Dialog Edit SMTP (mail_senders) */}
      {selectedSender && (
        <EditSenderDialog
          open={editSenderOpen}
          onClose={() => { setEditSenderOpen(false); setSelectedSender(null); }}
          sender={selectedSender}
          tenants={tenants}
          onSuccess={loadData}
        />
      )}
    </div>
  );
}

// Composant ligne de connexion
function ConnectionRow({ connection, onEdit, onRefresh }) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isSender = connection._source === "mail_senders";

  const handleTest = async (e) => {
    e.stopPropagation();
    setTesting(true);
    setTestResult(null);
    try {
      const url = isSender
        ? `${API}/api/mail/senders/${connection.id}/test`
        : `${API}/api/connections/${connection.id}/test`;
      const res = await axios.post(url);
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || "Erreur" });
    } finally {
      setTesting(false);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer "${connection.name}" ?`)) return;
    setDeleting(true);
    try {
      await axios.delete(isSender
        ? `${API}/api/mail/senders/${connection.id}`
        : `${API}/api/connections/${connection.id}`);
      onRefresh();
    } catch (err) {
      alert("Erreur de suppression");
    } finally {
      setDeleting(false);
    }
  };

  const handleSetDefault = async (e) => {
    e.stopPropagation();
    try {
      if (isSender) {
        await axios.put(`${API}/api/mail/senders/${connection.id}`, {
          is_default: true,
          tenant_id: connection._sender?.tenant_id,
        });
      } else {
        await axios.post(`${API}/api/connections/${connection.id}/set-default`);
      }
      onRefresh();
    } catch (err) {
      alert("Erreur");
    }
  };

  return (
    <div
      className="flex items-center justify-between px-4 py-3 hover:bg-zinc-800/30 transition-colors cursor-pointer border-b border-zinc-800/50 last:border-0"
      onClick={onEdit}
    >
      <div className="flex items-center gap-3">
        {connection.is_default && (
          <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
        )}
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-white">{connection.name}</span>
            <Badge 
              className={connection.status === "active" 
                ? "bg-emerald-500/20 text-emerald-400" 
                : "bg-zinc-500/20 text-zinc-400"
              }
            >
              {connection.status === "active" ? "Actif" : connection.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs text-zinc-500">
              {connection.scope_display?.activity || connection.scope_display?.global || "Global"}
            </span>
            {testResult && (
              <span className={`text-xs flex items-center gap-1 ${testResult.success ? "text-emerald-400" : "text-red-400"}`}>
                {testResult.success ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                {testResult.message?.slice(0, 30)}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleTest}
          disabled={testing}
          className="h-8 text-zinc-400 hover:text-white"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        </Button>
        {!connection.is_default && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSetDefault}
            className="h-8 text-zinc-400 hover:text-amber-400"
            title="Définir par défaut"
          >
            <Star className="w-4 h-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onEdit}
          className="h-8 text-zinc-400 hover:text-white"
        >
          <Settings className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDelete}
          disabled={deleting}
          className="h-8 text-zinc-400 hover:text-red-400"
        >
          {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

// Dialog d'édition d'une boîte sortante (mail_senders) — PUT/DELETE /api/mail/senders.
function EditSenderDialog({ open, onClose, sender, tenants = [], onSuccess }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && sender) {
      const s = sender.smtp || {};
      setForm({
        name: sender.name || "",
        email: sender.email || "",
        display_name: sender.display_name || "OmniHub IA",
        reply_to: sender.reply_to || "",
        is_default: !!sender.is_default,
        enabled: sender.enabled !== false,
        host: s.host || "smtp.mandrillapp.com",
        port: s.port || 587,
        username: s.username || "",
        password: s.password || "",
        use_tls: s.use_tls !== false,
      });
      setError(null);
    }
  }, [open, sender]);

  const upd = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const tenantLabel = tenants.find(t => t.slug === sender?.tenant_id)?.nom || sender?.tenant_id || "—";
  const fromIssue = fromEmailMandrillIssue(form.host, form.email);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    if (!form.email) { setError("Email expéditeur requis."); setSaving(false); return; }
    if (fromIssue) { setError(fromIssue); setSaving(false); return; }
    try {
      await axios.put(`${API}/api/mail/senders/${sender.id}`, {
        name: form.name,
        email: form.email,
        display_name: form.display_name || "OmniHub IA",
        reply_to: form.reply_to || null,
        tenant_id: sender.tenant_id,
        is_default: form.is_default,
        enabled: form.enabled,
        smtp: {
          host: form.host || "smtp.mandrillapp.com",
          port: Number(form.port) || 587,
          username: form.username || "",
          password: form.password || "",
          // Chiffrement DÉDUIT DU PORT (plus de case manuelle).
          ...smtpFlagsForPort(Number(form.port) || 587),
        },
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Supprimer la boîte sortante "${form.name}" ?`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/api/mail/senders/${sender.id}`);
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de suppression");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto pr-6">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-emerald-400" />
            Boîte sortante SMTP
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Tenant : <span className="text-zinc-200">{tenantLabel}</span> — lue par l'envoi de devis/emails de ce tenant.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          <div>
            <Label className="text-zinc-400">Nom</Label>
            <Input value={form.name || ""} onChange={(e) => upd("name", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400">Email expéditeur (From) <span className="text-red-400">*</span></Label>
            <Input type="email" value={form.email || ""} onChange={(e) => upd("email", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
          </div>
          {fromIssue && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <span className="text-xs text-amber-300">{fromIssue}</span>
            </div>
          )}
          <div>
            <Label className="text-zinc-400">Nom expéditeur</Label>
            <Input value={form.display_name || ""} onChange={(e) => upd("display_name", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400">Reply-To</Label>
            <Input type="email" value={form.reply_to || ""} onChange={(e) => upd("reply_to", e.target.value)} placeholder="ex : contact@exemple.invalid" className="bg-zinc-800/50 border-zinc-700 mt-1" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-zinc-400">Serveur SMTP</Label>
              <Input value={form.host || ""} onChange={(e) => upd("host", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
            </div>
            <div>
              <Label className="text-zinc-400">Port</Label>
              <Input type="number" value={form.port || ""} onChange={(e) => upd("port", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-400">Chiffrement (auto selon le port) :</span>
            <Badge className="bg-blue-500/15 text-blue-300 text-[11px]">{smtpModeLabel(smtpModeForPort(form.port))}</Badge>
            <span className="text-[11px] text-zinc-500">465 → SSL · 587 → STARTTLS · 25 → aucun</span>
          </div>
          <div>
            <Label className="text-zinc-400">Nom d'utilisateur</Label>
            <Input value={form.username || ""} onChange={(e) => upd("username", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
          </div>
          <div>
            <Label className="text-zinc-400">Mot de passe / API Key</Label>
            <Input type="password" value={form.password || ""} onChange={(e) => upd("password", e.target.value)} className="bg-zinc-800/50 border-zinc-700 mt-1" />
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.is_default} onChange={(e) => upd("is_default", e.target.checked)} className="w-4 h-4 rounded" />
              <span className="text-sm text-zinc-400">Par défaut (tenant)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!form.enabled} onChange={(e) => upd("enabled", e.target.checked)} className="w-4 h-4 rounded" />
              <span className="text-sm text-zinc-400">Activé</span>
            </label>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between">
          <Button variant="ghost" onClick={handleDelete} disabled={deleting} className="text-red-400 hover:text-red-300 mr-auto">
            {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Supprimer
          </Button>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog d'ajout de connexion
function AddConnectionDialog({ open, onClose, type, activities, assets, tenants = [], onSuccess }) {
  const [name, setName] = useState("");
  const [scopeType, setScopeType] = useState("global");
  const [activityId, setActivityId] = useState("");
  const [assetId, setAssetId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [config, setConfig] = useState({});
  const [isDefault, setIsDefault] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // SMTP : point d'entrée unifié vers les boîtes sortantes (mail_senders, scopé
  // tenant) — c'est ce que lit l'envoi transactionnel. Le "Périmètre" devient un
  // sélecteur de TENANT (pas une activité marketing).
  const isSmtp = type?.type === "smtp";

  useEffect(() => {
    if (open) {
      setName(`${type.name} - Nouvelle connexion`);
      setConfig({});
      setScopeType("global");
      setActivityId("");
      setAssetId("");
      setTenantId("");
      setIsDefault(isSmtp ? true : false);
      setError(null);
    }
  }, [open, type, isSmtp]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (isSmtp) {
        // SMTP → boîte sortante mail_senders, rattachée au tenant choisi.
        if (!tenantId) {
          setError("Sélectionnez un tenant (Périmètre) pour cette connexion SMTP.");
          setSaving(false);
          return;
        }
        if (!config.from_email) {
          setError("Email expéditeur requis.");
          setSaving(false);
          return;
        }
        // Garde-fou Mandrill : empêcher un from_email sur domaine non signé.
        const fromIssue = fromEmailMandrillIssue(config.host, config.from_email);
        if (fromIssue) {
          setError(fromIssue);
          setSaving(false);
          return;
        }
        await axios.post(`${API}/api/mail/senders`, {
          name,
          email: config.from_email,
          display_name: config.from_name || "OmniHub IA",
          reply_to: config.reply_to || null,
          tenant_id: tenantId,
          is_default: isDefault,
          enabled: true,
          smtp: {
            host: config.host || "smtp.mandrillapp.com",
            port: Number(config.port) || 587,
            username: config.username || "",
            password: config.password || "",
            // Chiffrement DÉDUIT DU PORT (465=SSL, 587=STARTTLS, 25=aucun) — plus de
            // case à cocher manuelle ; le backend déduit aussi côté connexion.
            ...smtpFlagsForPort(Number(config.port) || 587),
          },
        });
        onSuccess();
        onClose();
        return;
      }

      const scope = {};
      if (scopeType === "activity" && activityId) {
        scope.activity_id = activityId;
      } else if (scopeType === "asset" && assetId) {
        scope.asset_id = assetId;
      }

      await axios.post(`${API}/api/connections`, {
        integration_type: type.type,
        name,
        scope,
        config,
        is_default: isDefault
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de création");
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto pr-6">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Plus className="w-5 h-5 text-emerald-400" />
            Nouvelle connexion {type?.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {type?.description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Nom */}
          <div>
            <Label className="text-zinc-400">Nom de la connexion</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Clients TEF"
              className="bg-zinc-800/50 border-zinc-700 mt-1"
              required
            />
          </div>

          {/* Scope — SMTP : sélecteur de TENANT ; autres : périmètre marketing */}
          {isSmtp ? (
            <div>
              <Label className="text-zinc-400">Périmètre (tenant)</Label>
              <Select value={tenantId} onValueChange={setTenantId}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                  <SelectValue placeholder="Sélectionner le tenant" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {tenants.map(t => (
                    <SelectItem key={t.slug} value={t.slug}>{t.nom || t.slug}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                Boîte sortante rattachée à ce tenant — utilisée par l'envoi de devis/emails du tenant.
              </p>
            </div>
          ) : (
          <div>
            <Label className="text-zinc-400">Périmètre</Label>
            <Select value={scopeType} onValueChange={setScopeType}>
              <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="global">Global (toutes activités)</SelectItem>
                <SelectItem value="activity">Activité spécifique</SelectItem>
                <SelectItem value="asset">Asset spécifique</SelectItem>
              </SelectContent>
            </Select>
          </div>
          )}

          {!isSmtp && scopeType === "activity" && (
            <div>
              <Label className="text-zinc-400">Activité</Label>
              <Select value={activityId} onValueChange={setActivityId}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                  <SelectValue placeholder="Sélectionner une activité" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {activities.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {scopeType === "asset" && (
            <div>
              <Label className="text-zinc-400">Asset</Label>
              <Select value={assetId} onValueChange={setAssetId}>
                <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                  <SelectValue placeholder="Sélectionner un asset" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {assets.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name} ({a.type})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Champs de configuration */}
          <div className="space-y-3 pt-2 border-t border-zinc-800">
            <p className="text-sm font-medium text-zinc-300">Configuration</p>
            {/* SMTP : la case "TLS" manuelle est retirée — le chiffrement est déduit du port. */}
            {(type?.fields || []).filter(field => !(isSmtp && field.key === "use_tls")).map(field => (
              <div key={field.key}>
                <Label className="text-zinc-400">
                  {field.label}
                  {field.required && <span className="text-red-400 ml-1">*</span>}
                </Label>
                {field.type === "textarea" ? (
                  <textarea
                    value={config[field.key] || ""}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    placeholder={field.placeholder || ""}
                    className="w-full bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 mt-1 text-white text-sm min-h-[100px]"
                    required={field.required}
                  />
                ) : field.type === "boolean" ? (
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="checkbox"
                      checked={config[field.key] || false}
                      onChange={(e) => updateConfig(field.key, e.target.checked)}
                      className="w-4 h-4 rounded"
                    />
                    <span className="text-sm text-zinc-400">Activé</span>
                  </div>
                ) : (
                  <Input
                    type={field.type === "password" ? "password" : field.type === "number" ? "number" : "text"}
                    value={config[field.key] || ""}
                    onChange={(e) => updateConfig(field.key, e.target.value)}
                    placeholder={field.placeholder || ""}
                    className="bg-zinc-800/50 border-zinc-700 mt-1"
                    required={field.required}
                  />
                )}
              </div>
            ))}

            {/* SMTP : mode chiffrement déduit du port (lecture) + Reply-To + garde-fou */}
            {isSmtp && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-zinc-400">Chiffrement (auto selon le port) :</span>
                  <Badge className="bg-blue-500/15 text-blue-300 text-[11px]">
                    {smtpModeLabel(smtpModeForPort(config.port))}
                  </Badge>
                  <span className="text-[11px] text-zinc-500">465 → SSL · 587 → STARTTLS · 25 → aucun</span>
                </div>
                <div>
                  <Label className="text-zinc-400">Reply-To (réponses)</Label>
                  <Input
                    type="email"
                    value={config.reply_to || ""}
                    onChange={(e) => updateConfig("reply_to", e.target.value)}
                    placeholder="ex : contact@exemple.invalid"
                    className="bg-zinc-800/50 border-zinc-700 mt-1"
                  />
                  <p className="text-xs text-zinc-500 mt-1">
                    Adresse de réponse — peut être sur le domaine du tenant (non signé) sans risque.
                  </p>
                </div>
                {fromEmailMandrillIssue(config.host, config.from_email) && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-amber-300">{fromEmailMandrillIssue(config.host, config.from_email)}</span>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Default */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.target.checked)}
              className="w-4 h-4 rounded"
              id="is_default"
            />
            <Label htmlFor="is_default" className="text-zinc-400 cursor-pointer">
              Définir comme connexion par défaut
            </Label>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Créer la connexion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Dialog d'édition de connexion
function EditConnectionDialog({ open, onClose, connection, activities, assets, onSuccess }) {
  const [name, setName] = useState("");
  const [config, setConfig] = useState({});
  const [status, setStatus] = useState("active");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && connection) {
      setName(connection.name);
      setConfig(connection.config_display || {});
      setStatus(connection.status);
      setError(null);
    }
  }, [open, connection]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Nettoyer la config - ne pas envoyer les champs masqués non modifiés
      const cleanConfig = {};
      Object.entries(config).forEach(([key, value]) => {
        if (!key.endsWith("_configured") && value !== undefined) {
          // Ne pas envoyer les valeurs masquées
          if (typeof value === "string" && !value.includes("••••")) {
            cleanConfig[key] = value;
          }
        }
      });

      await axios.patch(`${API}/api/connections/${connection.id}`, {
        name,
        config: cleanConfig,
        status
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur de mise à jour");
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const typeConfig = connection?.type_config || {};

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto pr-6">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5 text-blue-400" />
            Modifier {connection?.name}
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            {typeConfig.description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Nom */}
          <div>
            <Label className="text-zinc-400">Nom de la connexion</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-800/50 border-zinc-700 mt-1"
              required
            />
          </div>

          {/* Scope (lecture seule) */}
          <div>
            <Label className="text-zinc-400">Périmètre</Label>
            <div className="bg-zinc-800/30 border border-zinc-700 rounded-md px-3 py-2 mt-1 text-zinc-300">
              {connection?.scope_display?.activity || connection?.scope_display?.asset || connection?.scope_display?.global || "Global"}
            </div>
          </div>

          {/* Status */}
          <div>
            <Label className="text-zinc-400">Statut</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-zinc-800/50 border-zinc-700 mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-800 border-zinc-700">
                <SelectItem value="active">Actif</SelectItem>
                <SelectItem value="inactive">Inactif</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Champs de configuration */}
          <div className="space-y-3 pt-2 border-t border-zinc-800">
            <p className="text-sm font-medium text-zinc-300">Configuration</p>
            {typeConfig.fields?.map(field => {
              const currentValue = config[field.key] || "";
              const isConfigured = config[`${field.key}_configured`];
              
              return (
                <div key={field.key}>
                  <Label className="text-zinc-400">
                    {field.label}
                    {isConfigured && (
                      <Badge className="ml-2 bg-emerald-500/20 text-emerald-400 text-xs">Configuré</Badge>
                    )}
                  </Label>
                  {field.type === "textarea" ? (
                    <textarea
                      value={currentValue}
                      onChange={(e) => updateConfig(field.key, e.target.value)}
                      placeholder={isConfigured ? "Laisser vide pour conserver" : field.placeholder || ""}
                      className="w-full bg-zinc-800/50 border border-zinc-700 rounded-md px-3 py-2 mt-1 text-white text-sm min-h-[100px]"
                    />
                  ) : (
                    <Input
                      type={field.type === "password" ? "password" : "text"}
                      value={typeof currentValue === "string" && currentValue.includes("••••") ? "" : currentValue}
                      onChange={(e) => updateConfig(field.key, e.target.value)}
                      placeholder={isConfigured ? "Laisser vide pour conserver" : field.placeholder || ""}
                      className="bg-zinc-800/50 border-zinc-700 mt-1"
                    />
                  )}
                </div>
              );
            })}
            
            {/* Section spéciale Zoho - Rafraîchir le token */}
            {connection?.integration_type === "zoho_workdrive" && (
              <ZohoTokenRefreshSection onSuccess={onSuccess} />
            )}
            {/* D20 : la rotation de secret ne passe PLUS par l'édition d'une copie
                tenant (chemin retiré). Elle vit dans la section « Autorité / Secrets »
                qui cible la fiche integrations globale par _id, sans bascule de tenant. */}
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-400">{error}</span>
            </div>
          )}
        </form>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
